/**
 * Similar-day forecast for EnergyOutlook.
 * Calls similar-day-forecast Edge Function; maps response to chartData shape.
 * Authority: SIMILAR_DAY_FORECAST_API (docs/contracts)
 */

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { SimilarDayForecastData } from '@/types/dashboard';

export interface SimilarDayChartPoint {
  time: string;
  timestamp: number;
  expected: number;
  best: number;
  worst: number;
  bandHeight?: number;
}

const STALE_MS = 30 * 1000;

function mapSimilarDayToChartPoints(data: SimilarDayForecastData | null): SimilarDayChartPoint[] {
  if (!data?.timestamps?.length || !data.expected_kwh?.length) return [];
  const ts = data.timestamps;
  const exp = data.expected_kwh;
  const best = data.best_kwh ?? exp;
  const worst = data.worst_kwh ?? exp;
  return ts.map((t, i) => {
    const date = new Date(t);
    const expected = i < exp.length ? Number(exp[i]) : 0;
    const b = i < best.length ? Number(best[i]) : expected;
    const w = i < worst.length ? Number(worst[i]) : expected;
    return {
      time: format(date, 'HH:mm'),
      timestamp: date.getTime(),
      expected,
      best: b,
      worst: w,
      bandHeight: Math.max(0, b - w),
    };
  });
}

export interface UseSimilarDayForecastResult {
  data: SimilarDayForecastData | null;
  chartData: SimilarDayChartPoint[];
  samplesUsed: number;
  matchedOn: string[];
  confidence: string;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSimilarDayForecast(
  propertyId: string | undefined,
  plantId: string | null,
  horizonHours = 24
): UseSimilarDayForecastResult {
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['similar-day-forecast', propertyId, plantId, horizonHours],
    queryFn: async (): Promise<SimilarDayForecastData | null> => {
      if (!propertyId) return null;
      const { data: res, error } = await supabase.functions.invoke('similar-day-forecast', {
        method: 'POST',
        body: { propertyId, plantId: plantId ?? undefined, horizonHours },
      });
      if (error) {
        const details = (res as { details?: string; hint?: string })?.details;
        const hint = (res as { hint?: string })?.hint;
        const msg = details ?? (error as Error).message;
        if (details ?? hint) {
          console.warn('[similar-day-forecast]', details ?? (error as Error).message, hint ? { hint } : '');
        }
        throw new Error(msg);
      }
      if (!res || res.samples_used === 0) return null;
      return res as SimilarDayForecastData;
    },
    enabled: !!propertyId,
    staleTime: STALE_MS,
    refetchInterval: STALE_MS,
  });

  const chartData = mapSimilarDayToChartPoints(data ?? null);

  return {
    data: data ?? null,
    chartData,
    samplesUsed: data?.samples_used ?? 0,
    matchedOn: data?.matched_on ?? [],
    confidence: data?.confidence ?? 'very_low',
    loading: isLoading,
    error: queryError ? (queryError as Error).message : null,
    refetch,
  };
}
